import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/srv-services/total-book/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetServicesCartTotalsPathInput = paths[url]['post']['parameters']['path'];
export type GetServicesCartTotalsBodyInput = paths[url]['post']['requestBody']['content']['application/json'];
export type GetServicesCartTotalsSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get a list of services that can be pre-booked/booked jointly with a specific service.
 */
export const useGetServicesCartTotals = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setServicesCartTotals, setIsLoadingServicesCartTotals } = useData();

    const request = async (pathProps: GetServicesCartTotalsPathInput, bodyProps: GetServicesCartTotalsBodyInput) => {
        setIsLoadingServicesCartTotals(true);

        const sessionId = getSessionId();

        try {
            const commonQueryString = new URLSearchParams(getCommonParameters()).toString();
            const sessionQueryString = `session_id=${encodeURIComponent(sessionId)}`;
            const fullQueryString = `${commonQueryString}&${sessionQueryString}`;

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', pathProps.uuid)}?${fullQueryString}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...bodyProps }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetServicesCartTotalsSuccessResponse = await response.json();

            setServicesCartTotals(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingServicesCartTotals(false);
        }
    };

    return { request };
};
